import React, { useRef, useEffect, useState } from 'react';
import './TechStack.css';

const sections = [
  {
    title: '🧱 Frontend',
    items: ['HTML5', 'JavaScript (ES6+)', 'React.js', 'CSS3', 'Tailwind CSS']
  },
  {
    title: '🔙 Backend',
    items: ['Node.js', 'Python', 'Java', 'C#']
  },
  {
    title: '🗃️ Database',
    items: ['MongoDB', 'MySQL']
  },
  {
    title: '🛠️ Tools & Platforms',
    items: ['Git & GitHub', 'Postman', 'VS Code']
  },
  {
    title: '🐳 DevOps Basics',
    items: ['Linux CLI', 'Docker (basic)', 'GitHub Actions (CI/CD)', 'AWS']
  },
  {
    title: '🎨 Design / UI',
    items: ['Figma', 'Canva (for quick mockups)']
  }
];

const skillsSections = [
  {
    title: '🤖 AI & Digital Skills',
    items: [
      'AI Prompt Engineering (ChatGPT, Gemini)',
      'AI-assisted coding (GitHub Copilot)',
      'No-code tools (Notion AI, Canva AI)'
    ]
  },
  {
    title: '💡 Professional Skills',
    items: [
      'Problem-solving & Debugging',
      'Communication & Teamwork',
      'Time Management',
      'Adaptability & Quick Learning'
    ]
  }
];

function TechStack() {
  const sectionRef = useRef([]);
  const skillsRef = useRef([]);
  const [visibleSections, setVisibleSections] = useState(Array(sections.length + skillsSections.length).fill(false));
  // Track visible items for each section (tools and skills)
  const [visibleItems, setVisibleItems] = useState(
    [
      ...sections.map(section => Array(section.items.length).fill(false)),
      ...skillsSections.map(section => Array(section.items.length).fill(false))
    ]
  );

  useEffect(() => {
    const allRefs = [...sectionRef.current, ...skillsRef.current];
    const observers = allRefs.map((ref, idx) => {
      if (!ref) return null;
      return new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => {
              if (prev[idx]) return prev; // Already visible, do nothing
              const updated = [...prev];
              updated[idx] = true;
              return updated;
            });
            // Animate items in this section one by one
            const itemsCount = idx < sections.length ? sections[idx].items.length : skillsSections[idx - sections.length].items.length;
            for (let i = 0; i < itemsCount; i++) {
              setTimeout(() => {
                setVisibleItems(prev => {
                  const updated = prev.map(arr => [...arr]);
                  updated[idx][i] = true;
                  return updated;
                });
              }, i * 120 + 100);
            }
          }
        },
        { threshold: 0.2 }
      );
    });
    allRefs.forEach((ref, idx) => {
      if (ref && observers[idx]) observers[idx].observe(ref);
    });
    return () => {
      observers.forEach((observer, idx) => {
        if (observer && allRefs[idx]) observer.disconnect();
      });
    };
  }, []);

  return (
    <div className="techstack-container">
      <div className="tools-block-bg">
        <h2>Tools & Technologies</h2>
        {sections.map((section, idx) => (
          <div
            className={`techstack-section${visibleSections[idx] ? ' visible' : ''}`}
            key={section.title}
            ref={el => (sectionRef.current[idx] = el)}
            style={{ transitionDelay: visibleSections[idx] ? `${idx * 0.18 + 0.1}s` : '0s' }}
          >
            <h3>{section.title.replace(/^[^a-zA-Z0-9]+\s*/, '')}</h3>
            <ul>
              {section.items.map((item, itemIdx) => {
                // Icon mapping for tools
                const iconMap = {
                  'HTML5': '/icons8-html-48.png',
                  'JavaScript (ES6+)': '/icons8-javascript-48.png',
                  'React.js': '/icons8-react-24.png',
                  'AWS': '/icons8-aws-48.png',
                  'Canva (for quick mockups)': '/icons8-canva-50.png',
                  'CSS3': '/icons8-css-48.png',
                  'Docker (basic)': '/icons8-docker-24.png',
                  'Figma': '/icons8-figma-48.png',
                  'Git & GitHub': '/icons8-git-48.png',
                  'Java': '/icons8-java-48.png',
                  'Linux CLI': '/icons8-linux-48.png',
                  'MongoDB': '/icons8-mongo-db-48.png',
                  'MySQL': '/icons8-mysql-48.png',
                  'Node.js': '/icons8-nodejs-48.png',
                  'Postman': '/postmon.webp',
                  'Tailwind CSS': '/icons8-tailwind-css-50.png',
                  'VS Code': '/icons8-vs-code-48.png',
                  'Python': '/icons8-python-48.png',
                  'C#': '/icons8-c-64.png',
                  'GitHub Actions (CI/CD)': '/CI_CD.png',
                };
                if (iconMap[item]) {
                  return (
                    <li
                      key={item}
                      className={visibleItems[idx][itemIdx] ? 'visible' : ''}
                      style={{ transitionDelay: visibleItems[idx][itemIdx] ? `${itemIdx * 0.13 + 0.1}s` : '0s' }}
                    >
                      <img src={process.env.PUBLIC_URL + iconMap[item]} alt={item} style={{ height: 32, verticalAlign: 'middle' }} />
                    </li>
                  );
                }
                return (
                  <li
                    key={item}
                    className={visibleItems[idx][itemIdx] ? 'visible' : ''}
                    style={{ transitionDelay: visibleItems[idx][itemIdx] ? `${itemIdx * 0.13 + 0.1}s` : '0s' }}
                  >
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {/* Separate Skills Section */}
      <div className="skills-block-separator"></div>
      <div className="skills-block-bg">
        <h2 className="skills-heading">Skills</h2>
        <div className="skills-sections-wrapper">
          {skillsSections.map((section, idx) => (
            <div
              className={`techstack-section skills-section${visibleSections[sections.length + idx] ? ' visible' : ''}`}
              key={section.title}
              ref={el => (skillsRef.current[idx] = el)}
              style={{ transitionDelay: visibleSections[sections.length + idx] ? `${(sections.length + idx) * 0.18 + 0.1}s` : '0s' }}
            >
              <h3>{section.title.replace(/^[^a-zA-Z0-9]+\s*/, '')}</h3>
              <ul>
                {section.items.map((item, itemIdx) => (
                  <li
                    key={item}
                    className={visibleItems[sections.length + idx][itemIdx] ? 'visible' : ''}
                    style={{ transitionDelay: visibleItems[sections.length + idx][itemIdx] ? `${itemIdx * 0.13 + 0.1}s` : '0s' }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TechStack; 